import { FC } from 'react';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import { FieldPath, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery } from '@/hooks/graphql/useQuery';
import { useMutation } from '@/hooks/graphql/useMutation';
import { NotFound } from '@/components/NotFound';
import { LoadingPage } from '@/components/LoadingPage';
import { useNotify } from '../../../../hooks/useNotify';
import { eachValidationErrorsForHookFrom, ServerValidationError } from '@/hooks/graphql/error/ServerValidationError';
import styles from './EditHealthCheckupInstitution.scss';
import { HealthCheckupInstitutionForm } from '../edit/parts/HealthCheckupInstitutionForm';
import { parseBackUrl } from '@/utils/url';
import { UPDATE_HEALTH_CHECKUP_INSTITUTION, UpdateHealthCheckupInstitutionQueryVariables } from './mutation';
import { HEALTH_CHECKUP_INSTITUTION } from './query';
import { Schema, schema } from '../edit/schema';

interface SearchParams {
  id: string;
}

export const EditHealthCheckupInstitution: FC = () => {
  const { id } = useParams<SearchParams>();
  const { data, loading } = useQuery(HEALTH_CHECKUP_INSTITUTION, {
    variables: { id: parseInt(id, 10) },
    fetchPolicy: 'no-cache'
  });

  const healthCheckupInstitution = data?.healthCheckupInstitution;

  if (!loading && !healthCheckupInstitution) {
    return <NotFound />;
  }

  return (
    <>
      <div className="l-title-wrap">
        <h1 className="m-title-main">健康診断実施機関の編集</h1>
      </div>
      <div className="l-contents-wrap">
        <div className="l-wrap-xl">
          <div className={styles.main}>
            {loading ? <LoadingPage /> : <FormWrapper id={parseInt(id, 10)} defaultValues={healthCheckupInstitution} />}
          </div>
        </div>
      </div>
    </>
  );
};

interface FormWrapperParams {
  id: number;
  defaultValues: Schema;
}

const FormWrapper: FC<FormWrapperParams> = ({ id, defaultValues }) => {
  const location = useLocation();
  const history = useHistory();
  const notify = useNotify();

  const methods = useForm<Schema>({
    defaultValues,
    resolver: yupResolver(schema)
  });
  const { handleSubmit, setError } = methods;

  const [editHealthCheckupInstitution] = useMutation<unknown, UpdateHealthCheckupInstitutionQueryVariables>(
    UPDATE_HEALTH_CHECKUP_INSTITUTION
  );

  const onSubmit: SubmitHandler<Schema> = async data => {
    try {
      await editHealthCheckupInstitution({
        variables: {
          input: {
            id,
            healthCheckupInstitutionId: data.healthCheckupInstitutionId,
            name: data.name,
            location: data.location
          }
        }
      });
      notify('変更が保存されました', 'success');
      history.push('/settings/health_checkup_institution');
    } catch (err) {
      if (err instanceof ServerValidationError) {
        eachValidationErrorsForHookFrom(err.validationErrors, schema, data, (path, error) => {
          setError(path as FieldPath<Schema>, error);
        });
        return;
      }
      throw err;
    }
  };

  return (
    <FormProvider {...methods}>
      <HealthCheckupInstitutionForm
        onSubmit={handleSubmit(onSubmit)}
        onCancel={() => history.push(parseBackUrl(location, '/settings/health_checkup_institution'))}
      />
    </FormProvider>
  );
};
